import React, { useEffect, useState } from 'react';
import './ProfileSettings.css'; // Assuming you have a CSS file for styling
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import ApiService from '../../services/ApiService';
import MangaApiService from '../../services/MangaApiService';
import { normalizeCdnUrl } from '../../services/CdnUtils';
import ErrorCodes from '../../services/ErrorCodes';
import FramePopup from '../profile/FramePopup';
import ChangePassPopup from '../profile/ChangePassPopup';
import AnimixPremiumPopup from '../../components/premium/AnimixPremiumPopup';
import AnimixPremiumPopupCancel from "../../components/premium/AnimixPremiumPopupCancel";
import profileIcon from '../../assets/ic_profile.svg';
import bookIcon from '../../assets/ic_book.svg';
import playIcon from '../../assets/ic_play_white.svg';
import settingsIcon from '../../assets/ic_settings.svg';
import premiumIcon from '../../assets/ic_premium_blank.svg';
import infoIcon from '../../assets/ic_info.svg';
import checkIcon from '../../assets/ic_check.svg';
import { THEMES } from '../../utils/themes';
import { themeGradient, useTheme } from '../../utils/ThemeContext';
import {
    PLAYER_MODE_ANIMIX,
    PLAYER_MODE_ANIMIX_KODIK,
    PLAYER_MODE_KODIK,
    getPreferredPlayerMode,
    savePreferredPlayerMode,
} from '../../components/player/playerSources';
import { getMangaPref, setMangaPref } from '../../utils/mangaPrefs';
import { ORDER_OPTIONS, TRANSLATION_OPTIONS } from '../manga/MangaCatalogFilters';

const SETTINGS_TABS = [
    { key: 'profile', label: 'Профиль', icon: profileIcon },
    { key: 'appearance', label: 'Оформление', icon: premiumIcon },
    { key: 'manga', label: 'Манга', icon: bookIcon },
    { key: 'player', label: 'Плеер', icon: playIcon },
    { key: 'access', label: 'Доступ', icon: settingsIcon },
    { key: 'subscription', label: 'Подписка', icon: premiumIcon },
];

const SettingsGroup = ({ icon, title, description, children }) => (
    <section className="settings-group">
        <header className="settings-group__head">
            <span className="settings-group__icon"><img src={icon} alt="" /></span>
            <div>
                <h2>{title}</h2>
                {description ? <p>{description}</p> : null}
            </div>
        </header>
        <div className="settings-group__rows">{children}</div>
    </section>
);

// One setting: title and hint on the left, its control on the right (or below when `stacked`).
const SettingsRow = ({ title, hint, stacked = false, children }) => (
    <div className={`settings-row ${stacked ? 'settings-row--stacked' : ''}`}>
        <div className="settings-row__text">
            <h3>{title}</h3>
            {hint ? <p>{hint}</p> : null}
        </div>
        <div className="settings-row__control">{children}</div>
    </div>
);

const SettingsSwitch = ({ label, checked, disabled, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={!!checked}
        disabled={disabled}
        className="settings-switch"
        onClick={() => onChange(!checked)}
    >
        <span className="ui-switch" aria-hidden="true" />
    </button>
);

const SITE_THEMES = THEMES.filter((theme) => theme.name !== 'null');

const PLAYER_OPTIONS = [
    { value: PLAYER_MODE_ANIMIX, label: 'Animix', hint: 'Наш плеер, лучшее качество' },
    { value: PLAYER_MODE_ANIMIX_KODIK, label: 'AnimixLite', hint: 'Наш плеер с видео Kodik' },
    { value: PLAYER_MODE_KODIK, label: 'Kodik', hint: 'Сторонний плеер' },
];

const QUALITY_OPTIONS = [
    { value: '', label: 'Авто' },
    { value: 'HD', label: '720p' },
    { value: 'FULL_HD', label: '1080p' },
    { value: 'QHD', label: '2K' },
    { value: 'ULTRA_HD', label: '4K' },
];

// Segmented control for a small set of exclusive options.
const SettingsSegmented = ({ label, options, value, onChange }) => (
    <div className="settings-seg" role="radiogroup" aria-label={label}>
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={value === option.value}
                title={option.hint}
                onClick={() => onChange(option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
);

const READ_DIRECTION_OPTIONS = [
    { value: 'ltr', label: 'Слева направо', hint: 'Как комиксы и манхва' },
    { value: 'rtl', label: 'Справа налево', hint: 'Как японская манга' },
];

const PAGE_FIT_OPTIONS = [
    { value: 'height', label: 'По высоте', hint: 'Страница целиком на экране' },
    { value: 'width', label: 'По ширине', hint: 'Крупнее, со скроллом' },
    { value: 'original', label: 'Оригинал', hint: 'Без масштабирования' },
];

const readStoredNumber = (key, fallback) => {
    const parsed = parseFloat(localStorage.getItem(key));
    return Number.isFinite(parsed) ? parsed : fallback;
};

const ProfileSettingsPage = () => {
    const { theme: currentTheme, setTheme } = useTheme();
    const [activeTab, setActiveTab] = useState('profile');
    const [description, setDescription] = useState('');
    const [nickname, setNickname] = useState('');
    const [selectedFrame, setSelectedFrame] = useState(null);
    const [telegram, setTelegram] = useState('');
    const [email, setEmail] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [error, setError] = useState(null);
    const [showFramePopup, setShowFramePopup] = useState(false);
    const [showChangePassPopup, setShowChangePassPopup] = useState(false);
    const [showSubscribePopup, setShowSubscribePopup] = useState(false);
    const [showSubscribeCancelPopup, setShowSubscribeCancelPopup] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const [frames, setFrames] = useState([]);
    const [allFrames, setAllFrames] = useState([]);
    const [account, setAccount] = useState(null);
    const [showEditPopup, setShowUploadPopup] = useState(false);
    const [frame, setFrame] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [dateSubscriptionPurchased, setDateSubscriptionPurchased] = useState(null);
    const [subscriptionType, setSubscriptionType] = useState('');
    const [autoSubscribe, setAutoSubscribe] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    const [hideSeriesPreviewEnabled, setHideSeriesPreview] = useState(localStorage.getItem("hideSeriesPreview") === "true");
    const [disableAutoSkip, setDisableAutoskip] = useState(localStorage.getItem("disableAutoSkip") === "true");
    const [disableAutoNext, setDisableAutoNext] = useState(localStorage.getItem("disableAutoNext") === "true");
    const [playerMode, setPlayerMode] = useState(getPreferredPlayerMode);
    const [preferredQuality, setPreferredQuality] = useState(localStorage.getItem("savedPrefQuality") || '');
    const [defaultVolume, setDefaultVolume] = useState(() => Math.round(readStoredNumber("volume", 1) * 100));
    const [mangaNsfwEnabled, setMangaNsfwEnabled] = useState(localStorage.getItem("mangaShowNsfw") === "true");
    const [mangaNsfwSaving, setMangaNsfwSaving] = useState(false);
    const [mangaPrefs, setMangaPrefsState] = useState(() => ({
        readDirection: getMangaPref('readDirection'),
        pageFit: getMangaPref('pageFit'),
        pageTransition: getMangaPref('pageTransition'),
        preloadNext: getMangaPref('preloadNext'),
        openFullscreen: getMangaPref('openFullscreen'),
        showComments: getMangaPref('showComments'),
        catalogOrder: getMangaPref('catalogOrder'),
        catalogTranslation: getMangaPref('catalogTranslation'),
    }));
    const updateMangaPref = (name, value) => {
        setMangaPref(name, value);
        setMangaPrefsState((current) => ({ ...current, [name]: value }));
    };
    const mangaSwitch = (name, label) => (
        <SettingsSwitch
            label={label}
            checked={mangaPrefs[name] === 'on'}
            onChange={(checked) => updateMangaPref(name, checked ? 'on' : 'off')}
        />
    );
    const [mangaNsfwError, setMangaNsfwError] = useState(null);
    const isMangaAllowed = MangaApiService.isAllowedDomain();

    // The NSFW switch lives on the manga server; localStorage is only this browser's last known value.
    useEffect(() => {
        if (!isMangaAllowed || !localStorage.getItem("sessionKey")) {
            return undefined;
        }
        let cancelled = false;
        MangaApiService.getUserSettings()
            .then((settings) => {
                if (cancelled || typeof settings?.showNsfw !== 'boolean') {
                    return;
                }
                setMangaNsfwEnabled(settings.showNsfw);
                localStorage.setItem("mangaShowNsfw", settings.showNsfw ? "true" : "false");
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isMangaAllowed]);



    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await ApiService.getSelf();

                if (data.type === "success") {
                    setAccount(data.data);
                    setNickname(data.data.nickname);
                    setDescription(data.data.description);
                    setSelectedFrame(data.data.selectedPicFrame);
                    setTelegram(data.data.telegram);
                    setDateSubscriptionPurchased(data.data.dateSubscriptionPurchased);
                    setSubscriptionType(data.data.subscriptionType);
                    setAutoSubscribe(data.data.autoSubscribe);
                    setSubscribed(data.data.subscribed);
                    setIsPrivate(data.data.private);

                    const framesData = await ApiService.getFrames();
                    const availableFrameIds = [...data.data.availablePicFrames, 0];
                    const availableFrames = framesData.filter(frame => availableFrameIds.includes(frame.id));
                    setFrames(availableFrames);
                    setAllFrames(framesData);

                    const selectedFrameId = data.data.selectedPicFrame || localStorage.getItem("selectedPicFrame");
                    const selectedFrame = framesData.find(frame => frame.id == selectedFrameId);
                    if (selectedFrame) {
                        setFrame(selectedFrame);
                    }

                    const emailData = await ApiService.getEmail();
                    setEmail(emailData.data);
                }

                const getCheckoutData = await ApiService.getNotifications(0);
                console.log(JSON.stringify(getCheckoutData.data));
            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        fetchData();
    }, []);

    // Resolves to true on success; the popup shows its own confirmation and closes itself.
    const handleChangePass = async (newPassword) => {
        try {
            const response = await ApiService.changePassword(newPassword);
            if (response.type === "error") {
                setPasswordError(response);
                return false;
            }
            setPasswordError(null);
            return true;
        } catch (error) {
            setPasswordError({ data: { code: -1 } });
            console.error('Error changing password:', error);
            return false;
        }
    };

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
    };

    const handleNicknameChange = (event) => {
        setNickname(event.target.value);
    };

    const handleTelegramChange = (event) => {
        setTelegram(event.target.value);
    };

    const handleMangaNsfwToggle = async (event) => {
        const nextValue = event.target.checked;
        setMangaNsfwEnabled(nextValue);
        localStorage.setItem("mangaShowNsfw", nextValue ? "true" : "false");
        setMangaNsfwSaving(true);
        setMangaNsfwError(null);
        try {
            await MangaApiService.updateUserSettings({ showNsfw: nextValue });
        } catch (error) {
            setMangaNsfwEnabled(!nextValue);
            localStorage.setItem("mangaShowNsfw", !nextValue ? "true" : "false");
            setMangaNsfwError(error?.message || 'Не удалось сохранить настройку NSFW для манги.');
        } finally {
            setMangaNsfwSaving(false);
        }
    };


    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };
    const handleUploadProfilePic = async (file) => {
        try {
            await ApiService.uploadProfilePic(file);
        } catch (error) {
            console.error('Error uploading profile picture:', error);
        }
    };

    const handleUploadBackground = async (file) => {
        try {
            await ApiService.uploadProfileBack(file);
        } catch (error) {
            console.error('Error uploading background image:', error);
        }
    };

    const handleSave = async () => {
        try {
            setSuccessMessage(null);
            const emailResponse = await ApiService.changeEmail(email);
            if (emailResponse.type === "error" | !email.includes("@") | email === null | email.length === 0) {
                setSuccessMessage("Ошибка: Введите корректную почту");
                return;
            }
            const response = await ApiService.editSelf({
                nickname,
                description,
                selectedPicFrame: selectedFrame,
                telegram,
                private: isPrivate
            });

            if (response.type === "error") {
                setError(response);
                setSuccessMessage("Ошибка: " + ErrorCodes.getText(response))
            } else {
                setError(null);
                setShowUploadPopup(false);
                setSuccessMessage("Изменения сохранены")

            }
        } catch (error) {
            console.error('Error saving profile:', error);
            setSuccessMessage("Ошибка (укажите почту)")
        }
    };

    const toggleAutoPay = async () => {
        try {
            const response = await ApiService.setAutopay(!autoSubscribe);

        } catch (error) {
            console.error('Error saving profile:', error);
        }
    };

    const handleFrameSelect = async (frame) => {
        setSelectedFrame(frame.id);
        setFrame(frame); // Ensure state updates correctly
        try {
            const response = await ApiService.editSelf({ nickname, description, selectedPicFrame: frame.id, telegram });
            if (response.type === "error") {
                setError(response);
            } else {
                setError(null);
                setShowUploadPopup(false);
            }
            setShowFramePopup(false);
        } catch (error) {
            console.error('Error selecting frame:', error);
        }
    };

    // Helper function to calculate next payment date
    const calculateNextPaymentDate = () => {
        if (!dateSubscriptionPurchased) return null;

        const purchaseDate = new Date(dateSubscriptionPurchased);
        let nextPaymentDate = new Date(purchaseDate);

        if (subscriptionType === 'YEARLY') {
            nextPaymentDate.setFullYear(nextPaymentDate.getFullYear() + 1);
        } else if (subscriptionType === 'MONTHLY') {
            nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
        }

        return nextPaymentDate.getTime();
    };

    // Helper function to format date in a readable format
    const formatDate = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    const hasPremium = subscribed || localStorage.getItem('subscribed') === 'true';

    const handlePlayerModeChange = (mode) => {
        setPlayerMode(mode);
        savePreferredPlayerMode(mode);
    };

    const handleQualityChange = (quality) => {
        setPreferredQuality(quality);
        if (quality) localStorage.setItem("savedPrefQuality", quality);
        else localStorage.removeItem("savedPrefQuality");
    };

    const handleVolumeChange = (percent) => {
        const next = Math.min(100, Math.max(0, Number(percent) || 0));
        setDefaultVolume(next);
        localStorage.setItem("volume", String(next / 100));
        if (next > 0) localStorage.setItem("muted", "false");
    };

    const handleLogout = () => {
        ['sessionKey', 'profileId', 'nickname', 'mod', 'editor', 'subscribed', 'selectedPicFrame'].forEach((key) => localStorage.removeItem(key));
        window.location.assign('/login');
    };

    const handleThemePick = (theme) => {
        if (!hasPremium) {
            setShowSubscribePopup(true);
            return;
        }
        setTheme(theme);
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'appearance':
                return (
                    <SettingsGroup
                        icon={premiumIcon}
                        title="Тема сайта"
                        description={hasPremium
                            ? 'Фон применяется ко всем страницам сайта'
                            : 'Темы доступны с подпиской animix premium'}
                    >
                        <div className="theme-grid" role="radiogroup" aria-label="Тема сайта">
                            {SITE_THEMES.map((theme) => {
                                const isActive = theme.name === currentTheme.name;
                                return (
                                    <button
                                        key={theme.name}
                                        type="button"
                                        role="radio"
                                        aria-checked={isActive}
                                        className={`theme-card${isActive ? ' is-active' : ''}`}
                                        onClick={() => handleThemePick(theme)}
                                    >
                                        <span className="theme-card__preview" style={{ background: themeGradient(theme) }} aria-hidden="true">
                                            <span className="theme-card__mock">
                                                <span className="theme-card__mock-nav" />
                                                <span className="theme-card__mock-body">
                                                    <span />
                                                    <span />
                                                    <span />
                                                </span>
                                            </span>
                                            {isActive ? <img className="theme-card__check" src={checkIcon} alt="" /> : null}
                                        </span>
                                        <span className="theme-card__name">{theme.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </SettingsGroup>
                );
            case 'player':
                return (
                    <>
                    <SettingsGroup icon={playIcon} title="Плеер" description="Как открывать серии по умолчанию">
                        <SettingsRow stacked title="Плеер по умолчанию" hint="Если серии нет в выбранном источнике, откроется доступный">
                            <SettingsSegmented label="Плеер по умолчанию" options={PLAYER_OPTIONS} value={playerMode} onChange={handlePlayerModeChange} />
                        </SettingsRow>
                        <SettingsRow stacked title="Качество по умолчанию" hint="Если качества нет у серии, плеер возьмёт ближайшее доступное">
                            <SettingsSegmented label="Качество по умолчанию" options={QUALITY_OPTIONS} value={preferredQuality} onChange={handleQualityChange} />
                        </SettingsRow>
                        <SettingsRow title="Громкость при запуске" hint="Плеер запомнит её и при изменении в самом видео">
                            <label className="settings-range">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="5"
                                    value={defaultVolume}
                                    aria-label="Громкость при запуске"
                                    onChange={(event) => handleVolumeChange(event.target.value)}
                                />
                                <span>{defaultVolume}%</span>
                            </label>
                        </SettingsRow>
                    </SettingsGroup>

                    <SettingsGroup icon={playIcon} title="Просмотр" description="Поведение во время серии">
                        <SettingsRow title="Автопереход к следующей серии" hint="На эндинге появится отсчёт, и следующая серия запустится сама">
                            <SettingsSwitch
                                label="Автопереход к следующей серии"
                                checked={!disableAutoNext}
                                onChange={(checked) => {
                                    setDisableAutoNext(!checked);
                                    localStorage.setItem("disableAutoNext", !checked);
                                }}
                            />
                        </SettingsRow>
                        <SettingsRow title="Скрывать превью серий" hint="Кадры из серий не будут показываться в списке">
                            <SettingsSwitch
                                label="Скрывать превью серий"
                                checked={hideSeriesPreviewEnabled}
                                onChange={(checked) => {
                                    setHideSeriesPreview(checked);
                                    localStorage.setItem("hideSeriesPreview", checked);
                                }}
                            />
                        </SettingsRow>
                        <SettingsRow title="Автопропуск опенингов и эндингов" hint="Размеченные фрагменты пропускаются сами. Выключает и автопереход">
                            <SettingsSwitch
                                label="Автопропуск опенингов и эндингов"
                                checked={!disableAutoSkip}
                                onChange={(checked) => {
                                    setDisableAutoskip(!checked);
                                    localStorage.setItem("disableAutoSkip", !checked);
                                }}
                            />
                        </SettingsRow>
                    </SettingsGroup>
                    </>
                );
            case 'profile':
                return (
                    <>
                        <SettingsGroup icon={profileIcon} title="Внешний вид" description="Изображения обновляются в течение суток">
                            <SettingsRow title="Изображение профиля" hint="PNG или JPG до 5 МБ">
                                <label htmlFor="profile-pic-upload" className="ui-btn ui-btn--secondary ui-btn--sm settings-upload">
                                    Загрузить
                                    <input id="profile-pic-upload" type="file" accept="image/*"
                                        onChange={(e) => handleUploadProfilePic(e.target.files[0])} />
                                </label>
                            </SettingsRow>
                            <SettingsRow title="Рамка профиля" hint="Рамки открываются за просмотр и достижения">
                                <div className="settings-frame">
                                    {frame && frame.url.length > 0 ? <img src={normalizeCdnUrl(frame.url)} alt="" /> : null}
                                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => setShowFramePopup(true)}>
                                        Сменить
                                    </button>
                                </div>
                            </SettingsRow>
                            <SettingsRow title="Фон профиля" hint="PNG или JPG до 5 МБ">
                                <label htmlFor="background-upload" className="ui-btn ui-btn--secondary ui-btn--sm settings-upload">
                                    Загрузить
                                    <input id="background-upload" type="file" accept="image/*"
                                        onChange={(e) => handleUploadBackground(e.target.files[0])} />
                                </label>
                            </SettingsRow>
                        </SettingsGroup>

                        <SettingsGroup icon={infoIcon} title="Данные профиля">
                            <SettingsRow stacked title="Никнейм" hint="Сменить никнейм можно только раз в три дня">
                                <input type="text" value={nickname} onChange={handleNicknameChange} placeholder="Введите никнейм" />
                            </SettingsRow>
                            <SettingsRow stacked title="Описание">
                                <input type="text" value={description} onChange={handleDescriptionChange} placeholder="Пара слов о себе" />
                            </SettingsRow>
                            <SettingsRow stacked title="Telegram" hint="Ссылка должна вести на профиль или канал">
                                <input type="text" value={telegram} onChange={handleTelegramChange} placeholder="https://t.me/username" />
                            </SettingsRow>
                            <SettingsRow stacked title="Почта" hint="Нужна для восстановления пароля и отправки чеков">
                                <input type="text" value={email} onChange={handleEmailChange} placeholder="mymail@animix.lol" />
                            </SettingsRow>
                            <SettingsRow title="Скрыть статистику" hint="Статистика будет скрыта для большинства пользователей, кроме лидерборда">
                                <SettingsSwitch label="Скрыть статистику" checked={isPrivate} onChange={setIsPrivate} />
                            </SettingsRow>
                        </SettingsGroup>

                        <div className="settings-actions">
                            <button type="button" className="ui-btn" onClick={handleSave}>Сохранить</button>
                            {successMessage != null ? <p className="settings-actions__status">{successMessage}</p> : null}
                        </div>
                    </>
                );

            case 'subscription':
                const nextPaymentDate = calculateNextPaymentDate();

                return (
                    <SettingsGroup
                        icon={premiumIcon}
                        title="Подписка"
                        description={subscribed
                            ? 'Ваша подписка активна'
                            : 'Подписка даёт много возможностей и поддерживает разработку animix'}
                    >
                        {subscribed ? (
                            <SettingsRow
                                title="Автопродление"
                                hint={autoSubscribe ? `Следующий платёж: ${formatDate(nextPaymentDate)}` : 'Подписка не продлится автоматически'}
                            >
                                <button
                                    type="button"
                                    className="ui-btn ui-btn--secondary ui-btn--sm"
                                    onClick={() => {
                                        if (autoSubscribe) {
                                            setShowSubscribeCancelPopup(true);
                                        } else {
                                            toggleAutoPay();
                                            setAutoSubscribe(!autoSubscribe);
                                        }
                                    }}
                                >
                                    {autoSubscribe ? 'Отключить' : 'Подключить'}
                                </button>
                            </SettingsRow>
                        ) : (
                            <SettingsRow title="animix premium" hint="Без рекламы, темы сайта, фон профиля и качество до 4K">
                                <button type="button" className="ui-btn ui-btn--sm" onClick={() => setShowSubscribePopup(true)}>
                                    Подключить
                                </button>
                            </SettingsRow>
                        )}
                    </SettingsGroup>
                );

            case 'access':
                return (
                    <SettingsGroup icon={settingsIcon} title="Доступ" description="Управление доступом к аккаунту">
                        <SettingsRow title="Пароль" hint="Новый пароль сразу заменит текущий">
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => setShowChangePassPopup(true)}>
                                Сменить
                            </button>
                        </SettingsRow>
                        <SettingsRow title="Аккаунт" hint="Выйти и войти под другим логином">
                            <a className="ui-btn ui-btn--secondary ui-btn--sm" href="/login">Сменить аккаунт</a>
                        </SettingsRow>
                        <SettingsRow title="Выйти" hint="Завершить сессию на этом устройстве">
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={handleLogout}>Выйти</button>
                        </SettingsRow>
                        <SettingsRow title="Удаление аккаунта" hint="Данные и история просмотра будут удалены безвозвратно">
                            <a className="ui-btn ui-btn--secondary ui-btn--sm settings-danger" href="/account-deletion">Удалить аккаунт</a>
                        </SettingsRow>
                    </SettingsGroup>
                );
            case 'manga':
                return (
                    <>
                    <SettingsGroup icon={bookIcon} title="Читалка" description="Как открываются главы">
                        <SettingsRow stacked title="Направление чтения" hint="Куда листать вперёд: клик по краю и стрелки на клавиатуре">
                            <SettingsSegmented label="Направление чтения" options={READ_DIRECTION_OPTIONS} value={mangaPrefs.readDirection} onChange={(value) => updateMangaPref('readDirection', value)} />
                        </SettingsRow>
                        <SettingsRow stacked title="Размер страницы" hint="Как вписывать страницу в экран">
                            <SettingsSegmented label="Размер страницы" options={PAGE_FIT_OPTIONS} value={mangaPrefs.pageFit} onChange={(value) => updateMangaPref('pageFit', value)} />
                        </SettingsRow>
                        <SettingsRow title="Открывать на весь экран" hint="Главы сразу открываются в полноэкранном режиме">
                            {mangaSwitch('openFullscreen', 'Открывать на весь экран')}
                        </SettingsRow>
                        <SettingsRow title="Анимация перелистывания" hint="Страницы сменяются с лёгким сдвигом">
                            {mangaSwitch('pageTransition', 'Анимация перелистывания')}
                        </SettingsRow>
                        <SettingsRow title="Предзагрузка следующей страницы" hint="Следующая страница скачивается заранее. Выключите при экономии трафика">
                            {mangaSwitch('preloadNext', 'Предзагрузка следующей страницы')}
                        </SettingsRow>
                        <SettingsRow title="Комментарии под главой" hint="Показывать обсуждение страницы в читалке">
                            {mangaSwitch('showComments', 'Комментарии под главой')}
                        </SettingsRow>
                    </SettingsGroup>

                    <SettingsGroup icon={bookIcon} title="Каталог" description="Фильтры, с которых открывается каталог">
                        <SettingsRow stacked title="Сортировка по умолчанию">
                            <SettingsSegmented label="Сортировка по умолчанию" options={ORDER_OPTIONS} value={mangaPrefs.catalogOrder} onChange={(value) => updateMangaPref('catalogOrder', value)} />
                        </SettingsRow>
                        <SettingsRow stacked title="Статус перевода по умолчанию">
                            <SettingsSegmented label="Статус перевода по умолчанию" options={TRANSLATION_OPTIONS} value={mangaPrefs.catalogTranslation} onChange={(value) => updateMangaPref('catalogTranslation', value)} />
                        </SettingsRow>
                    </SettingsGroup>

                    <SettingsGroup icon={bookIcon} title="Контент" description="Хранится в аккаунте">
                        <SettingsRow
                            title="Показывать NSFW мангу"
                            hint={mangaNsfwError || (!isMangaAllowed
                                ? 'Настройка доступна только на домене whitewhale.help.'
                                : 'Включайте, чтобы открывались NSFW тайтлы и главы.')}
                        >
                            <SettingsSwitch
                                label="Показывать NSFW мангу"
                                checked={mangaNsfwEnabled}
                                disabled={mangaNsfwSaving || !isMangaAllowed}
                                onChange={(checked) => handleMangaNsfwToggle({ target: { checked } })}
                            />
                        </SettingsRow>
                    </SettingsGroup>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <PageShell className="settings-page">
            <PageHeader title="Настройки" />
            <div className="profile-settings">
                <nav className="tabs" aria-label="Разделы настроек">
                    {SETTINGS_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={activeTab === tab.key ? 'active' : ''}
                            aria-current={activeTab === tab.key ? 'page' : undefined}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <img src={tab.icon} alt="" aria-hidden="true" />
                            {tab.label}
                        </button>
                    ))}
                </nav>
                <div className="tab-content-wrapper">
                    {renderContent()}
                </div>
            </div>
            {showFramePopup && (
                <FramePopup
                    availableFrames={frames}
                    allFrames={allFrames}
                    selectedFrame={selectedFrame}
                    handleFrameSelect={handleFrameSelect}
                    closePopup={() => setShowFramePopup(false)}
                />
            )}
            {showChangePassPopup && (
                <ChangePassPopup
                    handleChangePass={handleChangePass}
                    closePopup={() => { setShowChangePassPopup(false); setPasswordError(null); }}
                    error={passwordError}
                />
            )}

            {showSubscribePopup && (
                <AnimixPremiumPopup
                    nickname={account.nickname}
                    onClose={() => {
                        setShowSubscribePopup(false)
                    }}
                />
            )}

            {showSubscribeCancelPopup && (
                <AnimixPremiumPopupCancel
                    onCancel={() => {
                        toggleAutoPay();
                        setAutoSubscribe(!autoSubscribe);
                        setShowSubscribeCancelPopup(false);
                    }}
                    onClose={() => {
                        setShowSubscribeCancelPopup(false);
                    }}
                />
            )}
        </PageShell>
    );
};

export default ProfileSettingsPage;
